import { ContentErrorCode } from '@today/types'

/**
 * Error mapping and formatting shared by the content screens (TO-420
 * content-admin-ui REQ-013). The content-service envelope is
 * `{ error: { code, message } }`; the machine code is read from `error.code`.
 */

/** Translate a catalog key (react-i18next's `t`). */
export type Translate = (key: string, vars?: Record<string, string | number>) => string

/** Every code the content screens translate: the whole `ContentErrorCode` enum. */
export const CONTENT_ERROR_CODES: readonly string[] = Object.values(ContentErrorCode)

/** The form field a code lands on; any other code shows at form level. */
const FIELD_OF_CODE: Record<string, string> = {
	[ContentErrorCode.TEXT_CODE_TAKEN]: 'code',
	[ContentErrorCode.TYPE_CODE_TAKEN]: 'code',
	[ContentErrorCode.TYPE_NOT_FOUND]: 'typeCode',
	[ContentErrorCode.BODY_UNSAFE]: 'body',
	[ContentErrorCode.LOCALE_EXISTS]: 'locale',
	[ContentErrorCode.REASON_REQUIRED]: 'reason',
	[ContentErrorCode.VALIDITY_REQUIRED]: 'validFrom',
	[ContentErrorCode.VALIDITY_INVALID]: 'validFrom',
}

interface ErrorBody {
	code?: unknown
	message?: unknown
}

const bodyOf = (error: unknown): ErrorBody | null => {
	const data = (error as { response?: { data?: { error?: ErrorBody } } })?.response?.data
	return data && typeof data === 'object' ? (data.error ?? null) : null
}

/** The machine code of an API error, or null. */
export const contentErrorCode = (error: unknown): string | null => {
	const code = bodyOf(error)?.code
	return typeof code === 'string' && /^[A-Z_]+$/.test(code) ? code : null
}

/** HTTP status of an API error, or null. */
export const contentErrorStatus = (error: unknown): number | null =>
	(error as { response?: { status?: number } })?.response?.status ?? null

/** The server's own message (e.g. the `TEXT_NOT_AVAILABLE` list of codes), or ''. */
export const serverMessage = (error: unknown): string => {
	const message = bodyOf(error)?.message
	return typeof message === 'string' ? message : ''
}

/** The translated message for an API error; unknown or missing codes fall back to the generic message. */
export const contentErrorMessage = (error: unknown, t: Translate, exists: (key: string) => boolean): string => {
	const code = contentErrorCode(error)
	return code && exists(`errors.${code}`) ? t(`errors.${code}`) : t('errors.generic')
}

/** `{ field, message }` for an API error; `form` when no field relates. */
export const contentFieldError = (
	error: unknown,
	t: Translate,
	exists: (key: string) => boolean,
): { field: string; message: string } => {
	const code = contentErrorCode(error)
	return { field: (code && FIELD_OF_CODE[code]) || 'form', message: contentErrorMessage(error, t, exists) }
}

/** A timestamp as its UTC day, `YYYY-MM-DD` (the content screens show dates in UTC). */
export const day = (value: string | Date): string =>
	(typeof value === 'string' ? new Date(value) : value).toISOString().slice(0, 10)
